import { useLocation, useNavigate } from "react-router-dom";
import { AccentButton } from "@/components/buttonVariants";
import { useToast } from "@/hooks/useToast";
import { VERIFY_EMAIL_RESEND_ERROR } from "@/utils/toastMessages";
import { useEffect, useState } from "react";
import { publicApi } from "@/services/publicApi";
import fsvLogo from "@/images/achieveos-logo.png";
import fsvLogoDark from "@/images/achieveos-logo-dark.png";
import { MarketingPanel } from "@/pages/authentication/signup/components/MarketingPanel";
import { STEP1_PANEL_CONTENT, ADVISOR_STEP1_PANEL_CONTENT } from "@/constants/signupPanelContent";
import { Mail, ArrowRight } from "lucide-react";
import { signupVerificationEmailResent } from "@/utils/tracking";

const SESSION_TOKEN_KEY = "verify_session_token";

export const VerifyEmailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;
  const role = location.state?.role;
  const sessionToken = location.state?.sessionToken ?? sessionStorage.getItem(SESSION_TOKEN_KEY);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {}, [email, role]);

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      await publicApi.auth.resendVerificationEmail(sessionToken);
      signupVerificationEmailResent({ role });
      setResendCooldown(30);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch {
      showToast({ type: "error", ...VERIFY_EMAIL_RESEND_ERROR });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen font-inter bg-signup-form-bg bg-surface-page-bg">
      <div className="flex flex-1 min-h-0">
        {/* Left: light gradient bg, content centered */}
        <div className="flex-1 bg-signup-form-bg bg-surface-page-bg overflow-y-auto">
          <div className="flex flex-col items-center justify-center px-8 min-h-full py-8">
            <div className="w-full max-w-[448px] flex flex-col items-center">
              {/* Logo */}
              <img
                src={fsvLogo}
                alt="AchieveOS"
                className="h-[50px] w-[218px] max-w-full object-contain self-center mb-8 dark:hidden"
              />
              <img
                src={fsvLogoDark}
                alt="AchieveOS"
                className="h-[50px] w-[218px] max-w-full object-contain self-center mb-8 hidden dark:block"
              />

              {/* Card */}
              <div className="bg-surface-card border border-surface-border rounded-2xl w-full shadow-card-subtle overflow-hidden">
                <div className="px-8 pt-8 pb-8 flex flex-col items-center">
                  {/* Email icon */}
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 flex-shrink-0"
                    style={{
                      backgroundImage:
                        "linear-gradient(135deg, rgb(97, 95, 255) 0%, rgb(173, 70, 255) 100%)",
                    }}
                  >
                    <Mail className="text-white" size={28} strokeWidth={1.75} />
                  </div>

                  {/* Heading */}
                  <h1 className="text-2xl font-medium text-center text-theme-text-heading tracking-[0.07px] mb-3">
                    Check your email
                  </h1>

                  {/* Description */}
                  <p className="text-base text-center text-theme-text-muted leading-6 tracking-[-0.3px] mb-6 max-w-[358px]">
                    We have sent a verification link to your inbox. The link will expire in 10
                    minutes.
                  </p>

                  {/* Didn't receive label */}
                  <p className="text-sm text-center text-theme-text-muted tracking-[-0.15px] mb-4">
                    Didn&apos;t receive the email?
                  </p>

                  {/* Send again button */}
                  <AccentButton
                    onClick={handleResend}
                    disabled={loading || resendCooldown > 0}
                    className="w-full justify-center"
                  >
                    {loading
                      ? "Sending..."
                      : resendCooldown > 0
                        ? `Resend in ${resendCooldown}s`
                        : "Send again"}
                  </AccentButton>

                  {/* Try different email link */}
                  <button
                    onClick={() => navigate("/signup")}
                    className="mt-3 flex items-center gap-1 text-sm font-medium text-theme-accent hover:underline"
                  >
                    Try a different email
                    <ArrowRight size={14} />
                  </button>

                  {/* Sending to */}
                  {email && (
                    <p className="mt-6 text-xs text-center text-theme-text-muted tracking-[-0.15px]">
                      Sending to: <span className="font-medium text-theme-text-muted">{email}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Footer links */}
              <div className="flex items-center gap-6 mt-6">
                <button
                  onClick={() => navigate("/legal/terms", { state: { from: location.pathname } })}
                  className="text-sm text-theme-text-muted tracking-[-0.15px] hover:underline"
                >
                  Terms &amp; Conditions
                </button>
                <span className="text-theme-text-muted text-base">•</span>
                <button
                  onClick={() => navigate("/legal/privacy", { state: { from: location.pathname } })}
                  className="text-sm text-theme-text-muted tracking-[-0.15px] hover:underline"
                >
                  Privacy Policy
                </button>
                <span className="text-theme-text-muted text-base">•</span>
                <button
                  onClick={() => navigate("/legal/contact", { state: { from: location.pathname } })}
                  className="text-sm text-theme-text-muted tracking-[-0.15px] hover:underline"
                >
                  Contact Us
                </button>
              </div>
              <p className="text-xs text-theme-text-muted mt-2">
                &copy; 2026 Forward Share Ventures. All rights reserved.
              </p>
            </div>
          </div>
        </div>

        {/* Right: purple marketing panel */}
        <div className="hidden md:block">
          <MarketingPanel
            content={role === "advisor" ? ADVISOR_STEP1_PANEL_CONTENT : STEP1_PANEL_CONTENT}
          />
        </div>
      </div>
    </div>
  );
};
